import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCircle2, AlertTriangle, XCircle, Clock, Wand2, Siren } from "lucide-react";

const ICON = {
  run_completed: CheckCircle2, run_failed: XCircle, run_skipped: Clock, self_healed: Wand2, admin_alert: Siren,
};

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    if (n.run_id) navigate(`/runs/${n.run_id}`);
    else if (n.type === "admin_alert") navigate("/admin");
    else navigate("/config");
  };

  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" aria-label="Notifications"
          className="relative w-10 h-10 rounded-full flex items-center justify-center text-[#2A1E9E] hover:bg-[#3A2DB8]/10 transition-colors">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF6B3D] text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-xl border-border shadow-xl" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading font-semibold text-[#1E1560]">Run reports</span>
          {data.unread > 0 && <button onClick={readAll} className="text-xs text-[#3A2DB8] hover:text-[#FF6B3D]" data-testid="notifications-read-all">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-border">
          {data.items.length === 0 && <p className="p-6 text-sm text-secondary text-center">No reports yet — run an agent to get your first one.</p>}
          {data.items.map((n) => {
            const Icon = ICON[n.type] || AlertTriangle;
            const color = n.type === "admin_alert" ? "#E5325F" : n.type === "run_failed" ? "#E5325F" : n.type === "self_healed" ? "#7C3AED" : n.title.startsWith("Release ready") ? "#0F9D77" : "#FF8A3D";
            return (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-muted/60 transition-colors ${n.read ? "opacity-70" : ""}`}>
                <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#1E1560] truncate">{n.title}</span>
                  <span className="block text-xs text-secondary leading-snug">{n.body}</span>
                  <span className="block text-[10px] font-mono text-secondary mt-1">{n.created_at?.slice(0, 16).replace("T", " ")} UTC</span>
                </span>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#FF6B3D] shrink-0 mt-2" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
